import { z } from 'zod';
import { BlockSchema } from './blocks';
import { TenantBrandingSchema, type TenantBranding } from './theme';

/**
 * `TenantBrandingSchema` is the theme module's hand-rolled validator (it exposes
 * only `parse`/`safeParse`, not zod's chainable API), so wrap it as a zod field:
 * validate via the single source of truth, then narrow the type to `TenantBranding`.
 */
const BrandingFieldSchema = z
  .unknown()
  .refine((value) => TenantBrandingSchema.safeParse(value).success, { message: 'Invalid tenant branding.' })
  .transform((value) => TenantBrandingSchema.parse(value) as TenantBranding);

export const LayoutSchema = z.object({
  version: z.literal(1),
  grid: z.object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive()
  }),
  home: z.string().min(1),
  screens: z.array(z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    blocks: z.array(BlockSchema)
  })).min(1)
}).superRefine((layout, ctx) => {
  if (!layout.screens.some((screen) => screen.id === layout.home)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Home screen does not exist', path: ['home'] });
  }
  layout.screens.forEach((screen, screenIndex) => screen.blocks.forEach((block, blockIndex) => {
    if (block.rect.x + block.rect.w > layout.grid.cols || block.rect.y + block.rect.h > layout.grid.rows) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Block exceeds grid', path: ['screens', screenIndex, 'blocks', blockIndex, 'rect'] });
    }
  }));
});

export type Layout = z.infer<typeof LayoutSchema>;

/**
 * Immutable media metadata carried in a deployment snapshot (b10). Filled at
 * deploy time so a device can resolve `media_id` → bytes via the gateway without
 * ever seeing a PocketBase URL or token. `hash` is a strong ETag seed; `size`
 * bounds the response. Additive: pre-b10 snapshots simply omit `media`.
 */
export const SnapshotMediaItemSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['image', 'video']),
  mime: z.string().min(1),
  size: z.number().int().nonnegative(),
  hash: z.string().min(1)
});
export type SnapshotMediaItem = z.infer<typeof SnapshotMediaItemSchema>;

/** PocketBase record ids are 15 chars; accept a permissive but non-empty id. */
export const MediaIdSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9]+$/);

export const DeviceLayoutSnapshotSchema = z.object({
  layout_id: z.string().min(1),
  layout_version: z.number().int().nonnegative(),
  layout: LayoutSchema,
  // Optional for back-compat; treated as "no media" when absent.
  media: z.array(SnapshotMediaItemSchema).default([]),
  /**
   * Tenant branding frozen at deploy (f10 P3.4), so a device can honour the
   * logo/accent with no extra call. Optional + additive: pre-branding snapshots
   * simply omit it, and no secret is ever part of a branding record.
   */
  branding: BrandingFieldSchema.optional()
}).strict();
export type DeviceLayoutSnapshot = z.infer<typeof DeviceLayoutSnapshotSchema>;

/**
 * Top-level `media` metadata carried on the /device/layout response (b10/Step 1).
 * This is the additive field the frontend resolves asset URLs from: it lists
 * every media asset a device's deployment may fetch, with the immutable metadata
 * needed to build `<img src="/api/v1/device/media/:id">` without a PB URL.
 */
export const SnapshotMediaManifestSchema = z.object({
  media: z.array(SnapshotMediaItemSchema)
});
export type SnapshotMediaManifest = z.infer<typeof SnapshotMediaManifestSchema>;